import { centerOfMass } from '@turf/turf';
import * as d3 from 'd3';
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  RefObject,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFirstMountState } from 'react-use';

import {
  ensureNumber,
  GEO_MAP_MAX_SCALE,
  GEO_MAP_MIN_SCALE,
  getClientXYFromEvent,
  type Point,
  STATE_NAME_ABBREVIATION_MAP,
  useIsPortraitMode,
  useIsSmallDevice,
} from '#shared';

import { generateProjectionHash } from '../helpers';

import { FOCUS_VIEW } from './constants';
import { EFocusView } from './focusView.enum';
import { useInteraction, useProjectionPinchZoom, useTopography } from './hooks';
import {
  type ChangeEndEvent,
  type ChangeProgressEvent,
  type ChangeStartEvent,
  TransitionEventEmitter,
} from './transitionEventEmitter';

import type {
  Feature,
  FeatureCollection,
  Geometry,
  MultiPolygon,
  Polygon,
} from 'geojson';

type AnyPointerEvent = MouseEvent | TouchEvent | PointerEvent;

const paddingDefaults: Padding = {
  left: '5%',
  right: '5%',
  top: '5%',
  bottom: '5%',
};

// Multipliers applied on top of the d3 fitSize-derived scale, per focus view.
// 1 = exactly fitted; > 1 = zoomed in further.
const FOCUS_ZOOM_MULTIPLIER: Partial<Record<EFocusView, number>> = {
  [EFocusView.EUROPE]: 2,
};

// Max angular distance (radians, ~10°) a polygon's centroid may sit from the
// feature's largest polygon before it's treated as a far-flung outlier.
const FOCUS_OUTLIER_DISTANCE = 0.18;

/**
 * Drops far-flung polygons (overseas territories, distant island groups) from
 * multi-polygon features so a Focus View frames the country's main body — e.g.
 * mainland France rather than French Guiana, mainland Norway rather than
 * Svalbard/Bouvet Island. Features whose polygons are all close together (Italy,
 * the Gulf states, …) are returned untouched.
 */
const trimOutlyingPolygons = (features: Feature[]): Feature[] =>
  features.map(feature => {
    const { geometry } = feature;
    if (geometry?.type !== 'MultiPolygon' || geometry.coordinates.length < 2) {
      return feature;
    }
    const polygons = geometry.coordinates.map(coordinates => {
      const polygon: Polygon = { type: 'Polygon', coordinates };
      return {
        coordinates,
        area: d3.geoArea(polygon),
        centroid: d3.geoCentroid(polygon),
      };
    });
    const anchor = polygons.reduce((a, b) => (b.area > a.area ? b : a));
    const kept = polygons
      .filter(
        p =>
          d3.geoDistance(p.centroid, anchor.centroid) <= FOCUS_OUTLIER_DISTANCE,
      )
      .map(p => p.coordinates);
    if (kept.length === geometry.coordinates.length) {
      return feature;
    }
    const trimmed: MultiPolygon = { type: 'MultiPolygon', coordinates: kept };
    return { ...feature, geometry: trimmed };
  });

export interface Padding {
  left: number | `${number}%`;
  right: number | `${number}%`;
  top: number | `${number}%`;
  bottom: number | `${number}%`;
}

export interface GlobeBounds {
  height: number;
  width: number;
  x: number;
  xMax: number;
  y: number;
  yMax: number;
}

export interface GeoMapContextProps {
  // Projection state
  zoomByStep: (direction: 1 | -1) => void;

  projectionRef: RefObject<d3.GeoProjection>;
  // Bounds management
  bounds: GlobeBounds;
  setBounds: Dispatch<SetStateAction<GlobeBounds>>;
  getTopography: (scale: number) => FeatureCollection | null;

  mapRef: React.RefObject<HTMLCanvasElement | null>;
  interactionLayerRef: React.RefObject<
    SVGSVGElement | HTMLCanvasElement | null
  >;
  contextId: string;
  isProjectionChanging: boolean;
  projectionHash: string;
  usData: GeoJSON.FeatureCollection<
    Geometry,
    {
      NAME: string;
    }
  > | null;
  // Transition function for smooth projection changes
  startProjectionTransition: (
    projection: d3.GeoProjection,
    toRotation: [number, number, number],
    toScale: number,
    duration?: number,
  ) => void;
  // Event emitter for transition events
  transitionEventEmitter: TransitionEventEmitter;
  // Convenience hooks for transition events
  onChangeStart: (
    listener: (event: CustomEvent<ChangeStartEvent>) => void,
  ) => () => void;
  onChangeProgress: (
    listener: (event: CustomEvent<ChangeProgressEvent>) => void,
  ) => () => void;
  onChangeEnd: (
    listener: (event: CustomEvent<ChangeEndEvent>) => void,
  ) => () => void;

  topoLabels?: (
    | {
        feature: Feature<
          Geometry,
          {
            NAME: string;
          }
        >;
        centroid: [number, number];
        text: string;
      }
    | {
        feature: Feature<
          Geometry,
          {
            'ISO3166-1-Alpha-3': string;
            name: string;
          }
        >;
        centroid: [number, number];
        text: string;
      }
  )[];

  isInitialized: boolean;
}

export const GeoMapContext = createContext<GeoMapContextProps | undefined>(
  undefined,
);

export const useGeoMap = () => {
  const context = useContext(GeoMapContext);
  if (!context) {
    throw new Error('useGeoMap must be used within a GeoMapProvider');
  }
  return context;
};

export interface GeoMapProviderProps {
  initialFocus: EFocusView;
  contextId: string;
  padding?: Partial<Padding>;
  /**
   * Apply FOCUS_ZOOM_MULTIPLIER on top of the d3 fitSize-derived scale.
   * Defaults to true. The central widget passes false so its zoom matches
   * the raw fit and doesn't get the per-focus tweak meant for aside widgets.
   */
  applyFocusZoomMultiplier?: boolean;
}

export const GeoMapProvider = ({
  children,
  initialFocus = EFocusView.US_CONTINENTAL,
  contextId,
  padding,
  applyFocusZoomMultiplier = true,
}: PropsWithChildren<GeoMapProviderProps>) => {
  const {
    onChangeStart,
    onChangeProgress,
    onChangeEnd,
    startProjectionTransition,
    projectionRef,
    animationRef,
    transitionStateRef,
    transitionEventEmitter,
  } = useInteraction();
  const { usData, getTopography, isTopoReady, topoLabels, countriesData } =
    useTopography();
  const isSmallDevice = useIsSmallDevice();
  const isPortraitMode = useIsPortraitMode();

  const isFirstMount = useFirstMountState();
  const dragStartRef = useRef<Point | null>(null);
  const rotateStartRef = useRef<[number, number, number] | null>(null);
  const geoStartRef = useRef<[number, number] | null>(null);

  // Velocity tracking for drag inertia — sliding window of recent samples
  const velocitySamplesRef = useRef<{ t: number; lon: number; lat: number }[]>(
    [],
  );
  const inertiaRef = useRef<number | null>(null);

  const mapRef = useRef<HTMLCanvasElement>(null);
  const interactionLayerRef = useRef<SVGSVGElement | HTMLCanvasElement | null>(
    null,
  );
  const [isProjectionChanging, setIsProjectionChanging] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Generate initial projection hash - ref access during initialization is intentional

  const [projectionHash, setProjectionHash] = useState(() =>
    generateProjectionHash(projectionRef.current),
  );

  // Pinch-zoom handler, sets dragLockedRef while pinch is active to block drag-rotate
  const { dragLockedRef } = useProjectionPinchZoom({
    targetRef: interactionLayerRef as RefObject<Element>,
    fallbackRef: mapRef as RefObject<HTMLElement>,
    projectionRef,
    emitter: transitionEventEmitter,
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      const newProjectionHash = generateProjectionHash(projectionRef.current);
      if (newProjectionHash !== projectionHash) {
        setProjectionHash(newProjectionHash);
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [projectionHash, projectionRef]);

  const [bounds, setBounds] = useState<GlobeBounds>({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
    xMax: 0,
    yMax: 0,
  });

  const refreshBounds = useCallback(() => {
    if (!mapRef.current) {
      return;
    }
    // Update projection parameters
    // Define the projection
    // Update canvas size to match display size

    const canvas = mapRef.current;
    const { width, height } = canvas.getBoundingClientRect();

    // never translate the projection to [0,0]
    if (width <= 0 || height <= 0) {
      return;
    }

    const ctx = canvas.getContext('2d');
    const dpr = 1;
    if (ctx) {
      ctx.scale(dpr, dpr);
    }

    projectionRef.current.translate([width / 2, height / 2]);
    // Set canvas internal size to match display size for crisp rendering
    const bounds = d3.geoPath(projectionRef.current).bounds({ type: 'Sphere' });
    const upperLeft = bounds[0];
    const lowerRight = bounds[1];
    const x = Math.max(Math.floor(ensureNumber(upperLeft[0], 0)), 0);
    const y = Math.max(Math.floor(ensureNumber(upperLeft[1], 0)), 0);
    const xMax = Math.min(
      Math.ceil(ensureNumber(lowerRight[0], width)),
      width - 1,
    );
    const yMax = Math.min(
      Math.ceil(ensureNumber(lowerRight[1], height)),
      height - 1,
    );

    // Only update canvas dimensions if they've actually changed
    // Setting canvas.width/height clears the canvas, causing a blink
    const targetWidth = width * dpr;
    const targetHeight = height * dpr;

    if (
      targetWidth > 0 &&
      targetHeight > 0 &&
      (canvas.width !== targetWidth || canvas.height !== targetHeight)
    ) {
      // Save current canvas content before resizing (skip if canvas has zero dimensions)
      const hasContent = canvas.width > 0 && canvas.height > 0;
      let tempCtx: CanvasRenderingContext2D | null = null;
      let tempCanvas: HTMLCanvasElement | null = null;
      if (hasContent) {
        tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
          tempCtx.drawImage(canvas, 0, 0);
        }
      }

      // Resize canvas (this clears it)
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // Restore content scaled to new dimensions to prevent blink
      if (tempCtx && tempCanvas && ctx) {
        ctx.drawImage(tempCanvas, 0, 0, targetWidth, targetHeight);
      }
    }

    // Scale the canvas back down using CSS
    // canvas.style.width = `${width}px`;
    // canvas.style.height = `${height}px`;
    setBounds({
      x,
      y,
      xMax,
      yMax,
      width: mapRef.current.clientWidth,
      height: mapRef.current.clientHeight,
    });
  }, [projectionRef]);

  useEffect(() => {
    const unsubscribeOnChangeStart = onChangeStart(() => {
      setIsProjectionChanging(true);
    });
    const unsubscribeOnChangeEnd = onChangeEnd(() => {
      refreshBounds();
      setTimeout(() => {
        setIsProjectionChanging(false);
        // Mark as initialized after first mount and transition completes
        if (!isInitialized) {
          setIsInitialized(true);
        }
      }, 100);
    });
    return () => {
      unsubscribeOnChangeStart();
      unsubscribeOnChangeEnd();
    };
  }, [onChangeEnd, onChangeStart, refreshBounds, isInitialized]);

  // Determine transition duration based on projection scale - ref access for D3 projection state
  const transitionDuration =
    isFirstMount || projectionRef.current.scale() <= 300 ? 0 : 1000;

  useEffect(() => {
    if (!mapRef.current || !isTopoReady) {
      return;
    }

    let filteredGeoJSON: GeoJSON.FeatureCollection | null = null;
    if (initialFocus.startsWith('USA ')) {
      filteredGeoJSON = usData
        ? {
            type: 'FeatureCollection',
            features: usData.features.filter(f =>
              FOCUS_VIEW[initialFocus]?.includes(
                f.properties.NAME as keyof typeof STATE_NAME_ABBREVIATION_MAP,
              ),
            ),
          }
        : null;
    } else {
      filteredGeoJSON = countriesData
        ? {
            type: 'FeatureCollection',
            features: trimOutlyingPolygons(
              countriesData.features.filter(f =>
                (FOCUS_VIEW[initialFocus] as string[])?.includes(
                  f.properties.name,
                ),
              ),
            ),
          }
        : null;
    }
    const refresh = () => {
      if (!mapRef.current || !filteredGeoJSON) {
        return;
      }
      // Update projection parameters
      // Define the projection
      // Update canvas size to match display size

      const canvas = mapRef.current;
      const { width, height } = canvas.getBoundingClientRect();

      // never translate the projection to [0,0]
      if (width <= 0 || height <= 0) {
        return;
      }

      const ctx = canvas.getContext('2d');
      // TODO handle high DPI displays
      const dpr = 1;
      if (ctx) {
        ctx.scale(dpr, dpr);
      }

      projectionRef.current.translate([width / 4, height / 2]);
      // Set canvas internal size to match display size for crisp rendering

      // Scale the drawing context so everything draws at the correct size
      const center = centerOfMass(filteredGeoJSON).geometry.coordinates;
      const p = { ...paddingDefaults, ...padding };
      // TODO: resolve padding mechanism, so there will be well controlled edges
      const paddedTop =
        typeof p.top === 'string'
          ? p.top.endsWith('%')
            ? (height * parseFloat(p.top)) / 100
            : parseFloat(p.top)
          : p.top;
      const paddedBottom =
        typeof p.bottom === 'string'
          ? p.bottom.endsWith('%')
            ? (height * parseFloat(p.bottom)) / 100
            : parseFloat(p.bottom)
          : p.bottom;
      const paddedLeft =
        typeof p.left === 'string'
          ? p.left.endsWith('%')
            ? (width * parseFloat(p.left)) / 100
            : parseFloat(p.left)
          : p.left;
      const paddedRight =
        typeof p.right === 'string'
          ? p.right.endsWith('%')
            ? (width * parseFloat(p.right)) / 100
            : parseFloat(p.right)
          : p.right;
      const paddedWidth = width - paddedLeft - paddedRight;
      const paddedHeight = height - paddedTop - paddedBottom;
      // Calculate target projection parameters
      const tempProjection = d3.geoOrthographic();

      tempProjection.rotate([-center[0], -center[1]]);
      tempProjection.fitSize([paddedWidth, paddedHeight], filteredGeoJSON);
      const adjustedCenter: Point = [
        paddedWidth / 2 - ((paddedLeft - paddedRight) * paddedWidth) / width,
        paddedHeight / 2 - ((paddedTop - paddedBottom) * paddedHeight) / height,
      ];
      const adjustedRotation = tempProjection.invert?.(adjustedCenter);

      if (adjustedRotation) {
        tempProjection.rotate([-adjustedRotation[0], -adjustedRotation[1]]);
      }

      const fittedScale = tempProjection.scale();
      const focusMultiplier = applyFocusZoomMultiplier
        ? (FOCUS_ZOOM_MULTIPLIER[initialFocus] ?? 1)
        : 1;
      const baseScale = fittedScale * focusMultiplier;

      // let zoomMultiplier = 1;
      // if (isSmallDevice) {
      //   zoomMultiplier = isPortraitMode ? 3 : 2;
      // } else if (isPortraitMode) {
      //   zoomMultiplier = 2;
      // }

      // const targetScale = baseScale * zoomMultiplier;

      startProjectionTransition(
        projectionRef.current,
        tempProjection.rotate(),
        baseScale,
        transitionDuration,
      );

      refreshBounds();
    };
    const mapSizeObserver = new ResizeObserver(refresh);
    mapSizeObserver.observe(mapRef.current);
    addEventListener('resize', refresh);

    return () => {
      mapSizeObserver.disconnect();
      removeEventListener('resize', refresh);
    };
  }, [
    transitionDuration,
    contextId,
    countriesData,
    getTopography,
    initialFocus,
    isFirstMount,
    isPortraitMode,
    isSmallDevice,
    isTopoReady,
    projectionRef,
    refreshBounds,
    startProjectionTransition,
    transitionEventEmitter,
    usData,
    padding,
    applyFocusZoomMultiplier,
  ]);

  const ZOOM_FACTOR_PER_STEP = 1.25; // multiplicative factor for button zoom

  const zoomByStep = useCallback(
    (direction: 1 | -1) => {
      const currentScale = projectionRef.current.scale();
      const factor =
        direction === 1 ? ZOOM_FACTOR_PER_STEP : 1 / ZOOM_FACTOR_PER_STEP;
      const newScale = Math.max(
        GEO_MAP_MIN_SCALE,
        Math.min(GEO_MAP_MAX_SCALE, currentScale * factor),
      );
      startProjectionTransition(
        projectionRef.current,
        projectionRef.current.rotate(),
        newScale,
      );
    },
    [projectionRef, startProjectionTransition],
  );

  const handleWheel = useCallback(
    ({ sourceEvent: event }: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
      event.preventDefault();
      if (!event.deltaY) {
        return;
      }

      // Normalize deltaY across browsers and input devices
      let dy = event.deltaY;
      if (event.deltaMode === 1) {
        dy *= 40; // line mode
      } else if (event.deltaMode === 2) {
        dy *= 800; // page mode
      }

      // Multiplicative zoom: each 100px of scroll = ~15% zoom change
      const factor = Math.pow(1.0015, -dy);
      const currentScale = projectionRef.current.scale();
      const newScale = Math.max(
        GEO_MAP_MIN_SCALE,
        Math.min(GEO_MAP_MAX_SCALE, currentScale * factor),
      );

      projectionRef.current.scale(newScale);

      transitionEventEmitter.emit({
        type: 'change-progress',
        timestamp: Date.now(),
        rotation: projectionRef.current.rotate(),
        scale: newScale,
      });
    },
    [projectionRef, transitionEventEmitter],
  );

  // Pointer down handler - store initial position and rotation
  const handlePointerDown = useCallback(
    (rawEv: AnyPointerEvent) => {
      const map = mapRef.current;
      if (!map) {
        return;
      }

      // skip if pinch is active
      if (
        rawEv instanceof PointerEvent &&
        rawEv.pointerType === 'touch' &&
        dragLockedRef.current
      ) {
        return;
      }

      // Get mouse position relative to canvas
      const rect = map.getBoundingClientRect();
      const pt = getClientXYFromEvent(rawEv);
      if (!pt) {
        return;
      }

      if (
        typeof PointerEvent !== 'undefined' &&
        rawEv instanceof PointerEvent
      ) {
        (rawEv.target as Element).setPointerCapture?.(rawEv.pointerId);
      }

      // Cancel any ongoing inertia animation
      if (inertiaRef.current) {
        cancelAnimationFrame(inertiaRef.current);
        inertiaRef.current = null;
      }

      const canvasX = pt.x - rect.left;
      const canvasY = pt.y - rect.top;
      dragStartRef.current = [canvasX, canvasY];
      rotateStartRef.current = [...projectionRef.current.rotate()];
      geoStartRef.current =
        (projectionRef.current.invert?.([canvasX, canvasY]) as
          | [number, number]
          | undefined) ?? null;

      // Reset velocity tracking
      velocitySamplesRef.current = [];
    },
    [dragLockedRef, projectionRef],
  );

  // Pointer move handler - calculate rotation based on drag distance
  const handlePointerMove = useCallback(
    (rawEv: AnyPointerEvent) => {
      if (
        !dragStartRef.current ||
        !rotateStartRef.current ||
        !geoStartRef.current ||
        !mapRef.current
      ) {
        return;
      }

      const map = mapRef.current;
      const rect = map.getBoundingClientRect();
      const pt = getClientXYFromEvent(rawEv);
      if (!pt) {
        return;
      }

      const x = pt.x - rect.left;
      const y = pt.y - rect.top;

      const [startLambda, startPhi, startGamma] = rotateStartRef.current;
      const [geoLon0, geoLat0] = geoStartRef.current;

      // Temporarily reset rotation to start state so we can invert
      // the current cursor position in the same coordinate frame
      const currentRotation = projectionRef.current.rotate();
      projectionRef.current.rotate([startLambda, startPhi, startGamma]);
      const geoNow = projectionRef.current.invert?.([x, y]) as
        | [number, number]
        | undefined;

      if (!geoNow) {
        // Cursor moved outside the visible globe — restore and skip
        projectionRef.current.rotate(currentRotation);
        return;
      }

      // Compute rotation so the grabbed geographic point stays under the cursor
      const newLambda = startLambda + geoNow[0] - geoLon0;
      const newPhi = startPhi + geoNow[1] - geoLat0;
      const clampedPhi = Math.max(-90, Math.min(90, newPhi));

      // During drag, apply changes immediately for responsiveness
      // Cancel any ongoing animation first
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        // eslint-disable-next-line react-compiler/react-compiler
        animationRef.current = null;
        transitionStateRef.current = null;

        // Emit cancel event for user interaction
        transitionEventEmitter.emit({
          type: 'change-cancel',
          reason: 'user-interaction',
          timestamp: Date.now(),
        });
      }

      // Guard against NaN/Infinity
      if (!Number.isFinite(newLambda) || !Number.isFinite(clampedPhi)) {
        return;
      }

      // Update projection parameters immediately
      projectionRef.current.rotate([newLambda, clampedPhi, startGamma]);

      // Track velocity for inertia — keep a sliding window of recent rotation samples
      const now = performance.now();
      const rot = projectionRef.current.rotate();
      const WINDOW_MS = 80;
      velocitySamplesRef.current.push({ t: now, lon: rot[0], lat: rot[1] });
      // Trim samples older than the window
      const cutoff = now - WINDOW_MS;
      while (
        velocitySamplesRef.current.length > 1 &&
        velocitySamplesRef.current[0].t < cutoff
      ) {
        velocitySamplesRef.current.shift();
      }

      transitionEventEmitter.emit({
        type: 'change-progress',
        timestamp: Date.now(),
        rotation: projectionRef.current.rotate(),
        scale: projectionRef.current.scale(),
      });
    },
    [animationRef, projectionRef, transitionEventEmitter, transitionStateRef],
  );

  useEffect(() => {
    const interactionContainer = interactionLayerRef.current || mapRef.current;
    if (!interactionContainer) {
      return;
    }
    const zoom = d3
      .zoom<SVGSVGElement | HTMLCanvasElement, unknown>()
      .filter(event => {
        // Only allow zooming with the Ctrl or Meta key
        if (event.type === 'wheel') {
          return event.ctrlKey || event.metaKey;
        }
        // Block touch so pinch handler owns it
        if (
          event.pointerType === 'touch' ||
          event.type?.startsWith?.('touch')
        ) {
          return false;
        }
        // Allow mouse drag
        return true;
      })
      .on('start.zoom', () => {
        // Cancel any ongoing inertia
        if (inertiaRef.current) {
          cancelAnimationFrame(inertiaRef.current);
          inertiaRef.current = null;
        }
        // Cancel any ongoing animation when starting zoom
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
          transitionStateRef.current = null;

          // Emit cancel event for user interaction
          transitionEventEmitter.emit({
            type: 'change-cancel',
            reason: 'user-interaction',
            timestamp: Date.now(),
          });
        }
        transitionEventEmitter.emit({
          type: 'change-start',
          timestamp: Date.now(),
          rotation: projectionRef.current.rotate(),
          scale: projectionRef.current.scale(),
        });
      })
      .on('zoom', handleWheel)
      .on('end.zoom', () => {
        transitionEventEmitter.emit({
          type: 'change-end',
          timestamp: Date.now(),
          rotation: projectionRef.current.rotate(),
          scale: projectionRef.current.scale(),
        });
      });
    const drag = d3
      .drag<SVGSVGElement | HTMLCanvasElement, unknown>()
      .on('start', e => {
        const se = e.sourceEvent as PointerEvent | MouseEvent;

        // If a pinch is active, do not start a drag
        if (dragLockedRef.current) {
          return;
        }

        // Desktop mouse: require left button when available
        if (se instanceof MouseEvent) {
          if (typeof se.buttons === 'number' && se.buttons !== 1) {
            return;
          }
        }

        // PointerEvent path (covers touch & pen & mouse)
        if (se instanceof PointerEvent && se.pointerType === 'touch') {
          if (dragLockedRef.current) {
            // pinch or multi-touch = locked
            return;
          }
        }

        // Cancel any ongoing inertia
        if (inertiaRef.current) {
          cancelAnimationFrame(inertiaRef.current);
          inertiaRef.current = null;
        }
        // Cancel any ongoing animation when starting drag
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
          transitionStateRef.current = null;

          // Emit cancel event for user interaction
          transitionEventEmitter.emit({
            type: 'change-cancel',
            reason: 'user-interaction',
            timestamp: Date.now(),
          });
        }
      })
      .on('drag', e => {
        const se = e.sourceEvent as PointerEvent | MouseEvent;
        if (!dragStartRef.current) {
          transitionEventEmitter.emit({
            type: 'change-start',
            timestamp: Date.now(),
            rotation: projectionRef.current.rotate(),
            scale: projectionRef.current.scale(),
          });

          handlePointerDown(se as AnyPointerEvent);
        }

        // If a pinch is active, ignore drag moves
        if (dragLockedRef.current) {
          return;
        }

        // Touch: ignore moves if we don't have exactly one finger
        if (se instanceof PointerEvent && se.pointerType === 'touch') {
          if (dragLockedRef.current) {
            return;
          }
          se?.preventDefault?.();
        }

        handlePointerMove(se as AnyPointerEvent);
      })
      .on('end', () => {
        if (dragStartRef.current) {
          dragStartRef.current = null;
          rotateStartRef.current = null;
          geoStartRef.current = null;

          // Compute release velocity from the sliding window
          const samples = velocitySamplesRef.current;
          const now = performance.now();
          const STALE_THRESHOLD = 80; // ms — if cursor paused, no inertia
          let vLon = 0;
          let vLat = 0;

          if (
            samples.length >= 2 &&
            now - samples[samples.length - 1].t < STALE_THRESHOLD
          ) {
            const first = samples[0];
            const last = samples[samples.length - 1];
            const dt = last.t - first.t;
            if (dt > 0) {
              vLon = (last.lon - first.lon) / dt;
              vLat = (last.lat - first.lat) / dt;
            }
          }

          // Speed in degrees/ms — threshold to avoid micro-drifts
          const speed = Math.sqrt(vLon * vLon + vLat * vLat);
          const MIN_INERTIA_SPEED = 0.003; // deg/ms
          // Time-based friction: half-life ~300ms
          const HALF_LIFE = 300; // ms
          const DECAY = Math.LN2 / HALF_LIFE;

          if (speed > MIN_INERTIA_SPEED) {
            const startTime = performance.now();
            const v0Lon = vLon;
            const v0Lat = vLat;
            let lastFrame = startTime;

            const step = () => {
              const frameNow = performance.now();
              const elapsed = frameNow - startTime;
              const frameDt = frameNow - lastFrame;
              lastFrame = frameNow;

              // Exponential time-based decay: v(t) = v0 * e^(-k*t)
              const decay = Math.exp(-DECAY * elapsed);
              const vx = v0Lon * decay;
              const vy = v0Lat * decay;

              const frameSpeed = Math.sqrt(vx * vx + vy * vy);
              if (frameSpeed < 0.0003) {
                inertiaRef.current = null;
                transitionEventEmitter.emit({
                  type: 'change-end',
                  timestamp: Date.now(),
                  rotation: projectionRef.current.rotate(),
                  scale: projectionRef.current.scale(),
                });
                return;
              }

              const [curLon, curLat, curGamma] = projectionRef.current.rotate();
              const nextLon = curLon + vx * frameDt;
              const nextLat = Math.max(
                -90,
                Math.min(90, curLat + vy * frameDt),
              );
              projectionRef.current.rotate([nextLon, nextLat, curGamma]);

              transitionEventEmitter.emit({
                type: 'change-progress',
                timestamp: Date.now(),
                rotation: projectionRef.current.rotate(),
                scale: projectionRef.current.scale(),
              });

              inertiaRef.current = requestAnimationFrame(step);
            };

            inertiaRef.current = requestAnimationFrame(step);
          } else {
            transitionEventEmitter.emit({
              type: 'change-end',
              timestamp: Date.now(),
              rotation: projectionRef.current.rotate(),
              scale: projectionRef.current.scale(),
            });
          }
        }
      });
    const interactionLayer = d3.select(interactionContainer);
    interactionLayer.call(drag).call(zoom);
  }, [
    animationRef,
    dragLockedRef,
    handlePointerDown,
    handlePointerMove,
    handleWheel,
    projectionRef,
    transitionEventEmitter,
    transitionStateRef,
  ]);

  const contextValue: GeoMapContextProps = useMemo(
    () => ({
      projectionRef,
      bounds,
      setBounds,
      contextId,
      getTopography,
      usData: usData ?? null,
      mapRef,
      startProjectionTransition,
      transitionEventEmitter,
      interactionLayerRef,

      onChangeStart,
      onChangeProgress,
      onChangeEnd,
      zoomByStep,
      isProjectionChanging,
      projectionHash,
      topoLabels,
      isInitialized,
    }),
    [
      projectionRef,
      bounds,
      contextId,
      getTopography,
      usData,
      startProjectionTransition,
      transitionEventEmitter,
      onChangeStart,
      onChangeProgress,
      onChangeEnd,
      zoomByStep,
      isProjectionChanging,
      projectionHash,
      topoLabels,
      isInitialized,
    ],
  );

  return (
    <GeoMapContext.Provider value={contextValue}>
      {children}
    </GeoMapContext.Provider>
  );
};
